import { useAccount } from '@/hooks/useAccount';
import { AcceptJsCreditCardResult, useAcceptJs } from "@/hooks/useAcceptJs";
import { AccountLoadError, getAccountAccessToken } from '@/lib/account-session';
import {
    billToValidators, describeBillingFailure, describeBillTo, describeCard, EMPTY_BILL_TO, isBillToComplete,
    normalizeBillTo, paymentEnvironment, trimBillTo, US_STATES, type BillingFailure, type BillingSave,
} from '@/lib/billing';
import { ApiError, BillingInfo, BillToInfo, PqClient, PqPaymentInfo, PqServiceStatus, PqUpdatePaymentMethod } from "@/lib/pq-client";
import { ActionIcon, Alert, Anchor, Box, Button, ButtonProps, Card, Divider, Group, ScrollArea, Select, Stack, Text, TextInput, Title } from "@mantine/core";
import { useForm } from '@mantine/form';
import { IconChevronLeft, IconCircleCheck, IconCreditCard } from "@tabler/icons-react";
import { useAuth } from "@workos-inc/authkit-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import Reactivation from './Reactivation';

/**
 * Payment & billing: an overview of the card and address on file, plus two separate flows, per the
 * PQUser billing contract. "Replace card" sends a new AcceptUI card with the (usually unchanged) billing
 * address; "Edit billing information" updates the address without touching the card. Neither save
 * charges or reactivates the subscription.
 */
type View = 'overview' | 'replace-card' | 'edit-billing';

const Billing: React.FC = () => {
    const { getAccessToken, signIn } = useAuth();
    const { account, refreshAccount, signOut } = useAccount();
    const navigate = useNavigate();
    const [view, setView] = useState<View>('overview');
    const [savedBillTo, setSavedBillTo] = useState<BillToInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [loadAttempt, setLoadAttempt] = useState(0);
    // The card from a successful replacement, shown until /mobile/me is refreshed.
    const [savedCard, setSavedCard] = useState<PqPaymentInfo | null>(null);
    const [newCard, setNewCard] = useState<AcceptJsCreditCardResult | null>(null);
    const [addressExpanded, setAddressExpanded] = useState(false);
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);
    const mounted = useRef(true);
    const [failure, setFailure] = useState<BillingFailure | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    // A quote under review or an unresolved payment locks card and address edits.
    const [renewalBusy, setRenewalBusy] = useState(false);
    const [signingOut, setSigningOut] = useState(false);
    const [signOutError, setSignOutError] = useState<string | null>(null);
    const environment = paymentEnvironment(import.meta.env.VITE_ACCEPTJS_ENVIRONMENT, import.meta.env.DEV);
    const paymentConfigured = Boolean(environment && import.meta.env.VITE_ACCEPTJS_API_LOGIN_ID && import.meta.env.VITE_ACCEPTJS_CLIENT_KEY);
    const cardOnFile = savedCard ?? account.paymentInfo;
    const isActive = account.serviceStatus === PqServiceStatus.Active;

    const form = useForm<BillToInfo>({
        mode: 'uncontrolled',
        initialValues: EMPTY_BILL_TO,
        validate: billToValidators,
        transformValues: trimBillTo,
    });

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        let current = true;
        setLoading(true);
        setLoadError(null);
        void (async () => {
            try {
                const token = await getAccountAccessToken(getAccessToken);
                const response = await new PqClient().get<BillingInfo | null>(token, '/account/me/billing-information', {
                    returnResponse: true, signal: controller.signal,
                });
                if (!current) return;
                if (!response.ok) {
                    setLoadError(describeBillingFailure('billing-information', response.status, response.problem).message);
                    return;
                }
                if (!response.data || typeof response.data !== 'object' || !('billTo' in response.data)) {
                    setLoadError('Billing details are unavailable. Please try again.');
                    return;
                }
                setSavedBillTo(response.data.billTo);
            } catch (error) {
                if (!current || (error instanceof DOMException && error.name === 'AbortError')) return;
                setLoadError(error instanceof AccountLoadError ? error.message : 'Unable to load billing details. Check your connection and try again.');
            } finally {
                if (current) setLoading(false);
            }
        })();
        return () => { current = false; controller.abort(); };
    }, [getAccessToken, loadAttempt]);

    useEffect(() => {
        if (!success) return;
        const timer = window.setTimeout(() => setSuccess(null), 6000);
        return () => window.clearTimeout(timer);
    }, [success]);

    /** Reloads the card (/mobile/me) and billing address from the API. */
    const reload = useCallback(() => {
        setSavedCard(null);
        setLoadAttempt((attempt) => attempt + 1);
        void refreshAccount().catch(() => setLoadError('Unable to refresh account details. Please try again.'));
    }, [refreshAccount]);

    const handleSignOut = async () => {
        setSigningOut(true);
        setSignOutError(null);
        try {
            await signOut();
        } catch {
            if (!mounted.current) return;
            setSignOutError('Unable to sign out. Please try again.');
            setSigningOut(false);
        }
    };

    const openView = (next: View) => {
        setFailure(null);
        setSuccess(null);
        setNewCard(null);
        form.setValues(normalizeBillTo(savedBillTo));
        form.clearErrors();
        // Show the full form straight away when the saved address can't be sent as-is.
        setAddressExpanded(next === 'replace-card' && !isBillToComplete(savedBillTo));
        setView(next);
    };

    /** Runs one save at a time (both flows share the lock) and maps failures per the contract. */
    const runSave = async <T,>(save: BillingSave, send: (token: string) => Promise<{ ok: true; data: T } | { ok: false; status: number; problem: ApiError['problem'] }>, done: (data: T) => void) => {
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setFailure(null);
        try {
            const token = await getAccountAccessToken(getAccessToken);
            const response = await send(token);
            if (!mounted.current) return;
            if (response.ok) {
                done(response.data);
                return;
            }
            handleFailure(describeBillingFailure(save, response.status, response.problem));
        } catch (error) {
            if (!mounted.current) return;
            handleFailure(error instanceof AccountLoadError && error.requiresSignIn
                ? describeBillingFailure(save, 401, null)
                // No response: a network failure or timeout, so the outcome is unknown.
                : describeBillingFailure(save, 0, null));
        } finally {
            savingRef.current = false;
            if (mounted.current) setSaving(false);
        }
    };

    const handleFailure = (result: BillingFailure) => {
        setFailure(result);
        // The form keeps the customer's edits on every failure.
        if (result.fieldErrors) {
            form.setErrors(result.fieldErrors);
            setAddressExpanded(true);
        }
        // Card nonces are single-use, so a failed card save needs the card entered again.
        if (view === 'replace-card') setNewCard(null);
        if (result.next === 'reload') reload();
    };

    const saveNewCard = (billTo: BillToInfo) => {
        const card = newCard;
        if (!card) return;
        void runSave<PqPaymentInfo | null>('payment-method', (token) => {
            const update: PqUpdatePaymentMethod = { opaqueData: card.nonce, billTo };
            return new PqClient().put<PqPaymentInfo | null>(token, '/account/me/payment-method', update, { returnResponse: true });
        }, (paymentInfo) => {
            // The 200 response is the new card summary; no need to refetch /mobile/me.
            setSavedCard(paymentInfo ?? {
                cardLast4: card.cardLast4, cardType: card.cardType, cardholderName: null,
                expiryMonth: card.expiryMonth, expiryYear: card.expiryYear,
            });
            setSavedBillTo(billTo);
            setNewCard(null);
            setView('overview');
            setSuccess('Your new card is on file.');
        });
    };

    const saveBillingAddress = (billTo: BillToInfo) => {
        void runSave<BillingInfo | null>('billing-information', (token) =>
            new PqClient().put<BillingInfo | null>(token, '/account/me/billing-information', { billTo }, { returnResponse: true }),
        (saved) => {
            setSavedBillTo(saved?.billTo ?? billTo);
            setView('overview');
            setSuccess('Your billing address has been saved.');
        });
    };

    const failureAlert = failure && (
        <Alert color="red" role="alert" title={failure.next === 'support' ? 'Contact support' : undefined}>
            <Stack gap="xs">
                <Text size="sm">{failure.message}</Text>
                {failure.next === 'sign-in' && (
                    <Button size="xs" color="pq-red" w="fit-content" onClick={() => void signIn().catch(() => undefined)}>Sign in again</Button>
                )}
                {failure.next === 'support' && (
                    <Text size="sm">
                        Email <Anchor href="mailto:mobilesupport@parcelquest.com">mobilesupport@parcelquest.com</Anchor> or
                        call <Anchor href="tel:+18337681224">(833) 768-1224</Anchor>.
                    </Text>
                )}
            </Stack>
        </Alert>
    );

    const addressFields = (
        <Stack gap="sm">
            <Group grow align="flex-start">
                <TextInput label="First name" required disabled={saving} key={form.key('firstName')} {...form.getInputProps('firstName')} />
                <TextInput label="Last name" required disabled={saving} key={form.key('lastName')} {...form.getInputProps('lastName')} />
            </Group>
            <TextInput label="Company" disabled={saving} key={form.key('company')} {...form.getInputProps('company')} />
            <TextInput label="Street address" required disabled={saving} key={form.key('address')} {...form.getInputProps('address')} />
            <TextInput label="City" required disabled={saving} key={form.key('city')} {...form.getInputProps('city')} />
            <Group grow align="flex-start">
                <Select label="State" required searchable data={US_STATES} disabled={saving} key={form.key('state')} {...form.getInputProps('state')} />
                <TextInput label="ZIP" required disabled={saving} inputMode="numeric" key={form.key('zip')} {...form.getInputProps('zip')} />
            </Group>
            <TextInput label="Country" value="United States" disabled readOnly />
        </Stack>
    );

    const header = (title: string, onBack: (() => void) | null) => (
        <Group justify="center" align="center" style={{ flexShrink: 0, position: 'relative' }}>
            {onBack && (
                <ActionIcon style={{ position: 'absolute', top: 12, left: 0 }} onClick={onBack} disabled={saving} variant="transparent" size="md" aria-label="Back">
                    <IconChevronLeft color="black" />
                </ActionIcon>
            )}
            <Title order={4} my={10}>{title}</Title>
        </Group>
    );

    let content: React.ReactNode;
    if (view === 'replace-card') {
        content = (
            <form onSubmit={form.onSubmit(saveNewCard, () => setAddressExpanded(true))}>
                <Stack gap="lg">
                    {cardOnFile && (
                        <Card withBorder padding="sm" bg="gray.0">
                            <Group gap="sm" wrap="nowrap">
                                <IconCreditCard />
                                <Text size="sm">Current card: {describeCard(cardOnFile)}</Text>
                            </Group>
                        </Card>
                    )}
                    <Stack gap="xs">
                        <Title order={5}>1. New card</Title>
                        {newCard ? (
                            <Card withBorder padding="sm">
                                <Group gap="sm" wrap="nowrap">
                                    <IconCreditCard />
                                    <Text size="sm">{describeCard(newCard)}</Text>
                                </Group>
                            </Card>
                        ) : (
                            <></>
                        )}
                        {paymentConfigured && environment ? (
                            <AcceptJsButton
                                apiLoginId={import.meta.env.VITE_ACCEPTJS_API_LOGIN_ID}
                                publicClientKey={import.meta.env.VITE_ACCEPTJS_CLIENT_KEY}
                                environment={environment}
                                onCreditCardSubmitted={(result) => { setFailure(null); setNewCard(result); }}
                                disabled={saving}
                                formButtonText="Use this card"
                                formHeaderText="New card"
                                variant={newCard ? 'default' : 'filled'}
                                color="pq-red"
                            >
                                {newCard ? 'Use a different card' : 'Enter new card'}
                            </AcceptJsButton>
                        ) : <Text c="red" role="alert">Card updates are currently unavailable. Please contact support.</Text>}
                    </Stack>
                    <Stack gap="xs">
                        <Group justify="space-between">
                            <Title order={5}>2. Billing address</Title>
                            {!addressExpanded && <Button variant="subtle" size="compact-sm" color="pq-red" onClick={() => setAddressExpanded(true)} disabled={saving}>Change</Button>}
                        </Group>
                        {addressExpanded ? addressFields : (
                            <Card withBorder padding="sm">
                                {describeBillTo(savedBillTo).map((line) => <Text key={line} size="sm">{line}</Text>)}
                            </Card>
                        )}
                    </Stack>
                    {failureAlert}
                    <Button type="submit" size="lg" color="pq-red" loading={saving} disabled={!newCard}>Save new card</Button>
                    {!newCard && <Text size="xs" c="dimmed" ta="center">Enter a new card to continue.</Text>}
                    <Button variant="subtle" color="gray" disabled={saving} onClick={() => setView('overview')}>Cancel</Button>
                </Stack>
            </form>
        );
    } else if (view === 'edit-billing') {
        content = (
            <form onSubmit={form.onSubmit(saveBillingAddress)}>
                <Stack gap="lg">
                    <Card withBorder padding="sm" bg="gray.0">
                        <Group gap="sm" wrap="nowrap">
                            <IconCreditCard />
                            <Text size="sm">{cardOnFile ? describeCard(cardOnFile) : 'No card on file'} <Text span c="dimmed">(unchanged)</Text></Text>
                        </Group>
                    </Card>
                    {addressFields}
                    {failureAlert}
                    <Button type="submit" size="lg" color="pq-red" loading={saving}>Save billing address</Button>
                    <Button variant="subtle" color="gray" disabled={saving} onClick={() => setView('overview')}>Cancel</Button>
                </Stack>
            </form>
        );
    } else {
        const addressLines = describeBillTo(savedBillTo);
        content = (
            <Stack gap="lg">
                {!isActive && (
                    <>
                        <Reactivation onBusyChange={setRenewalBusy} />
                        <Text size="xs" c="dimmed">Updating your card or billing address doesn't reactivate your subscription on its own.</Text>
                        <Divider />
                    </>
                )}
                {success && <Alert color="green" icon={<IconCircleCheck />}>{success}</Alert>}
                {loadError && (
                    <Alert color="red" role="alert">
                        <Stack gap="xs">
                            <Text size="sm">{loadError}</Text>
                            <Button size="xs" variant="default" w="fit-content" onClick={reload}>Try again</Button>
                        </Stack>
                    </Alert>
                )}
                <Stack gap="xs">
                    <Title order={5}>Card on file</Title>
                    <Card withBorder padding="sm">
                        <Group gap="sm" wrap="nowrap">
                            <IconCreditCard />
                            <Text size="sm">{cardOnFile ? describeCard(cardOnFile) : 'No card on file'}</Text>
                        </Group>
                    </Card>
                    <Button color="pq-red" disabled={loading || !!loadError || !paymentConfigured || renewalBusy} onClick={() => openView('replace-card')}>
                        {cardOnFile ? 'Replace card' : 'Add card'}
                    </Button>
                    {!paymentConfigured && <Text size="sm" c="red">Card updates are currently unavailable. Please contact support.</Text>}
                </Stack>
                <Divider />
                <Stack gap="xs">
                    <Title order={5}>Billing address</Title>
                    <Card withBorder padding="sm">
                        {loading ? <Text size="sm" c="dimmed">Loading…</Text>
                            : addressLines.length > 0 ? addressLines.map((line) => <Text key={line} size="sm">{line}</Text>)
                            : <Text size="sm" c="dimmed">No billing address on file</Text>}
                    </Card>
                    <Button variant="default" disabled={loading || !!loadError || renewalBusy} onClick={() => openView('edit-billing')}>Edit billing information</Button>
                    {renewalBusy && <Text size="xs" c="dimmed">Finish or cancel the renewal before changing your card or address.</Text>}
                </Stack>
                <Button variant="subtle" color="gray" disabled={loading} onClick={reload}>Refresh details</Button>
                {/* Inactive accounts can't reach the side menu, so the overview offers Sign out; never mid-edit. */}
                {!isActive && (
                    <>
                        <Divider />
                        <Button variant="subtle" color="pq-red" loading={signingOut} disabled={renewalBusy} onClick={() => void handleSignOut()}>Sign out</Button>
                        {signOutError && <Text size="sm" c="red" ta="center" role="alert">{signOutError}</Text>}
                    </>
                )}
            </Stack>
        );
    }

    const titles: Record<View, string> = { overview: 'Payment & billing', 'replace-card': 'Replace card', 'edit-billing': 'Edit billing information' };
    const onBack = view !== 'overview' ? () => setView('overview') : isActive ? () => navigate('/', { replace: true }) : null;

    return (
        <Stack h="100%" mih={0} px="md" bg="white" style={{ overflow: 'hidden' }}>
            {header(titles[view], onBack)}
            <ScrollArea type="never" scrollbars="y" style={{ flex: '1 1 0', minHeight: 0 }} viewportProps={{ tabIndex: 0, 'aria-label': titles[view] }}>
                <Box pt={5} pb={60}>{content}</Box>
            </ScrollArea>
        </Stack>
    );
};

export default Billing;

interface AcceptJsButtonProps extends Omit<ButtonProps, 'onClick' | 'ref'> {
    apiLoginId: string;
    publicClientKey: string;
    environment: 'sandbox' | 'production';
    onCreditCardSubmitted: (result: AcceptJsCreditCardResult) => void;
    formHeaderText?: string;
    formButtonText?: string;
}

/**
 * Opens Authorize.Net's hosted AcceptUI lightbox (SAQ A): card number, expiry and code never touch
 * the app. Its own billing fields are hidden; the billing address comes from the app's form.
 */
const AcceptJsButton: React.FC<AcceptJsButtonProps> = ({
    apiLoginId,
    publicClientKey,
    environment,
    onCreditCardSubmitted,
    formHeaderText,
    formButtonText,
    disabled,
    children,
    ...buttonProps
}: AcceptJsButtonProps) => {
    const { buttonRef, scriptLoaded, scriptError, paymentError, creditCardResult, reset, retry } = useAcceptJs({ environment });

    useEffect(() => {
        if (creditCardResult) {
            onCreditCardSubmitted(creditCardResult);
            reset();
        }
    }, [creditCardResult, onCreditCardSubmitted, reset]);

    return (
        <Stack gap="xs">
            <Button
                ref={buttonRef}
                className="AcceptUI"
                data-apiloginid={apiLoginId}
                data-clientkey={publicClientKey}
                data-billingaddressoptions={JSON.stringify({ show: false, required: false })}
                data-paymentoptions={JSON.stringify({ showCreditCard: true, showBankAccount: false })}
                data-acceptuiformbtntxt={formButtonText}
                data-acceptuiformheadertxt={formHeaderText}
                data-responsehandler="acceptJsCallback"
                disabled={!scriptLoaded || !!scriptError || disabled}
                {...buttonProps}
            >
                {children ?? formButtonText}
            </Button>
            {(scriptError || paymentError) && <Text c="red" role="alert">{scriptError || paymentError}</Text>}
            {scriptError && <Button variant="subtle" disabled={disabled} onClick={retry}>Retry payment service</Button>}
        </Stack>
    );
}
